/**
 * FlowExportModal - Modal for exporting a flow as PDF
 */

import { useState } from 'react';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { cn } from '@/utils/cn';
import { flowsService, type FlowExportPdfRequest } from '@/services/flows';
import { ApiClientError } from '@/services/api';
import { toast } from '@/components/ui/Toaster';

interface FlowExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  flowName?: string;
}

type PaperSize = 'auto' | 'a4' | 'letter';

const PAPER_SIZES: { value: PaperSize; label: string; description: string }[] = [
  { value: 'auto', label: 'Auto', description: 'Best fit for content' },
  { value: 'a4', label: 'A4', description: '210 x 297 mm' },
  { value: 'letter', label: 'Letter', description: '8.5 x 11 inches' },
];

export default function FlowExportModal({
  isOpen,
  onClose,
  flowId,
  flowName,
}: FlowExportModalProps) {
  const [includeAccountBreakdown, setIncludeAccountBreakdown] = useState(true);
  const [paperSize, setPaperSize] = useState<PaperSize>('auto');
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const options: FlowExportPdfRequest = {
        includeAccountBreakdown,
        paperSize,
      };

      const response = await flowsService.exportPdf(flowId, options);

      // Open download URL in new tab
      window.open(response.downloadUrl, '_blank');
      toast.success('PDF export started. Your download should begin shortly.');
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 404) {
          setError('Flow not found. It may have been deleted.');
        } else if (err.status === 502 || err.status === 503) {
          setError('Export service is temporarily unavailable. Please try again later.');
        } else if (err.status === 504) {
          setError('Export request timed out. Please try again.');
        } else {
          setError(err.detail || 'Failed to export PDF. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isExporting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="export-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 id="export-modal-title" className="text-lg font-semibold text-gray-900">
              Export as PDF
            </h2>
            {flowName && (
              <p className="mt-1 text-sm text-gray-500">
                Export &quot;{flowName}&quot; to a downloadable PDF file.
              </p>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {error && (
              <Alert variant="error" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Include account breakdown toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="include-breakdown" className="text-sm font-medium text-gray-700">
                  Include account breakdown
                </label>
                <p className="text-xs text-gray-500">
                  Add a table showing flow by account
                </p>
              </div>
              <button
                id="include-breakdown"
                type="button"
                role="switch"
                aria-checked={includeAccountBreakdown}
                onClick={() => setIncludeAccountBreakdown(!includeAccountBreakdown)}
                className={cn(
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  includeAccountBreakdown ? 'bg-primary-600' : 'bg-gray-200'
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    includeAccountBreakdown ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
            </div>

            {/* Paper size selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paper Size
              </label>
              <div className="grid grid-cols-3 gap-2">
                {PAPER_SIZES.map((size) => (
                  <button
                    key={size.value}
                    type="button"
                    onClick={() => setPaperSize(size.value)}
                    className={cn(
                      'px-3 py-2 rounded-lg border text-center transition-colors',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500',
                      paperSize === size.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <div className="font-medium text-sm">{size.label}</div>
                    <div className="text-xs text-gray-500">{size.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Info note */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600">
                The PDF will include a summary of income, expenses, and net flow for the selected period.
                The download link will expire in 15 minutes.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleExport}
              isLoading={isExporting}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              }
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
